import { useMasterData } from '@/contexts/profile';
import { ProductStatus } from '@/layouts/shared';
import { BUSINESS, organizationJsonLd, priceValidUntil } from '@/constants/business';
import { SEO_INDEXABLE, ROBOTS_INDEX, ROBOTS_NOINDEX } from '@/constants/seo';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { memo } from 'react';
export enum HeadChannel {
  Graph = 'Graph',
  Twitter = 'Twitter',
}

type HeadProps = {
  title: string;
  /** Optional title suffix; the organization and Product brand stay unchanged. */
  titleBrand?: string;
  image?: string;
  description?: string;
  blacklist?: HeadChannel[];
  type?: string;
  productCode?: string;
  productId?: number;
  price?: number;
  category?: string;
  /** Tên danh mục hiển thị (vd "Nhẫn Nữ") — ưu tiên hơn `category` (mã) trong JSON-LD */
  categoryName?: string;
  /** Chất liệu trang sức, vd "14K Gold" */
  material?: string;
  /** @deprecated không còn dùng — priceValidUntil tự tính 30 ngày tới */
  updated?: string;
  status?: string;
  handle?: string;
  seo?: any;
  /** true => noindex trang này (vd bài WordPress đã tắt index trong Yoast) */
  noindex?: boolean;
  /** mô tả dài cho schema Product (JSON-LD), khác meta description ngắn; không hiện cho khách */
  schemaDescription?: string;
};

// Tiêu đề/mô tả từ WordPress có sẵn entity (&#8211; &nbsp; &amp;) → giải mã để Google hiển thị đúng
const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
};
const decodeEntities = (s?: string) =>
  (s || '')
    .replace(/&#(x?)([0-9a-f]+);/gi, (_, x, n) => String.fromCodePoint(parseInt(n, x ? 16 : 10)))
    .replace(/&([a-z]+);/gi, (m, n) => ENTITIES[n.toLowerCase()] ?? m)
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const MasterDataDefault = {
  author: 'Công ty TNHH An Thư The Diamond Store',
  keywords:
    'Kim cương, an thu, kim cương an thư, nhẫn kim cương, vòng cổ kim cương, bông tai kim cương, dây chuyền kim cương, giá kim cương, hoa tai kim cuong, kim cuong cao cap, lac tay kim cuong, lắc tay kim cương, trang sức kim cương, vong tay kim cuong, vòng tay kim cương, vong tay kim cuong',
  copyright: 'Công ty TNHH An Thư The Diamond Store',
  generator: 'Công ty TNHH An Thư The Diamond Store',
};
const MetaTag = ({
  title: rawTitle,
  titleBrand = BUSINESS.brand,
  description: rawDescription,
  image,
  type,
  productCode,
  productId,
  price,
  category,
  categoryName,
  material,
  status,
  blacklist = [],
  handle,
  seo,
  noindex = false,
  schemaDescription,
}: HeadProps) => {
  const masterData: any = useMasterData();
  const title = decodeEntities(rawTitle);
  const description = rawDescription ? decodeEntities(rawDescription) : rawDescription;

  // useRouter (không dùng Router singleton): lúc SSR singleton chưa có asPath → canonical mọi trang bị trỏ về trang chủ
  const router = useRouter();
  const getLocale = (l: string | undefined) => {
    if (l === 'en') return 'en_US';
    return 'vi_VN';
  };

  const fbDomainVerification = process.env.FB_DOMAIN_VERIFY;
  const FB_PAGE_ID = process.env.FB_PAGE_ID || '';
  const APP_URL = process.env.APP_URL || '';

  // trailingSlash: true trong next.config → URL chuẩn luôn có "/" ở cuối
  const withSlash = (p: string) => {
    const path = (p || '/').split('#')[0];
    return path.endsWith('/') ? path : path + '/';
  };
  const cleanPath = withSlash(router?.asPath?.split('?')[0] ?? '/');
  const href = APP_URL + (handle ? withSlash(handle) : cleanPath);
  const BRAND = BUSINESS.brand;
  const titleSuffix = decodeEntities(titleBrand) || BRAND;
  // title đã kết thúc bằng brand ("| An Thư" hoặc "| An Thư Kim Cương") thì không nối thêm
  const fullTitle =
    title.endsWith(titleSuffix) || /\|\s*An Thư$/.test(title) ? title : `${title} | ${titleSuffix}`;
  const metaTitle = seo?.title || fullTitle;
  // Google cắt meta description ~155–160 ký tự → cắt tại ranh giới từ để không bị "…" giữa chữ
  const clampDescription = (text: string, max = 158) => {
    const clean = text.replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max - 1);
    return cut.slice(0, Math.max(cut.lastIndexOf(' '), 80)).replace(/[,;:.\-–]+$/, '') + '…';
  };
  const metaDescription = clampDescription(seo?.description || description || BUSINESS.description);
  return (
    <NextHead>
      <meta charSet="utf-8" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <title>{fullTitle}</title>
      <meta
        name="google-site-verification"
        content="+nxGUDJ4QpAZ5l9Bsjdi102tLVC21AIh5d1Nl23908vVuFHs34="
      ></meta>
      <meta name="title" content={metaTitle} />
      {/* /en/… chỉ dịch giao diện, nội dung vẫn tiếng Việt → bản sao của trang /vi → noindex (canonical đã trỏ về bản tiếng Việt) */}
      <meta
        name="robots"
        content={
          SEO_INDEXABLE && !noindex && router.locale !== 'en' ? ROBOTS_INDEX : ROBOTS_NOINDEX
        }
      />
      <meta name="description" content={metaDescription} />
      <meta name="google" content="nositelinkssearchbox" />
      <meta name="googlebot" content="notranslate" />
      <meta
        name="keywords"
        content={seo?.keywords || masterData?.company_info?.keywords || MasterDataDefault.keywords}
      />
      <meta
        name="copyright"
        content={masterData?.company_info?.copyright || MasterDataDefault.copyright}
      />
      <meta name="author" content={masterData?.company_info?.author || MasterDataDefault.author} />
      <meta
        name="GENERATOR"
        content={masterData?.company_info?.generator || MasterDataDefault.generator}
      />
      <meta name="resource-type" content="Document" />
      <meta name="distribution" content="Global" />
      <meta name="revisit-after" content="1 days" />
      <meta name="facebook-domain-verification" content={fbDomainVerification} />
      <meta itemProp="name" content={metaTitle} />
      <meta itemProp="description" content={metaDescription} />
      {image && <meta itemProp="image" content={image} />}
      <meta httpEquiv="audience" content="General" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1"></meta>
      <meta httpEquiv="x-dns-prefetch-control" content="on" />
      <meta property="fb:pages" content={FB_PAGE_ID} />
      <link rel="canonical" href={href} />
      {/* Chưa có bản dịch tiếng Anh thật (/en/* vẫn nội dung tiếng Việt) → chỉ khai báo vi, không khai báo en */}
      <link rel="alternate" hrefLang="vi" href={href} />
      <link rel="alternate" hrefLang="x-default" href={href} />
      {!blacklist.includes(HeadChannel.Graph) && (
        <>
          <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
          <meta property="og:url" content={href} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          {image && <meta property="og:image" content={image} />}

          <meta property="og:site_name" content={BUSINESS.name} />
          <meta property="og:locale" content={getLocale(router?.locale)} />
          {type === 'product' && price !== undefined && (
            <>
              <meta property="product:price:amount" content={String(price)} />
              <meta property="product:price:currency" content="VND" />
            </>
          )}
        </>
      )}
      {!blacklist.includes(HeadChannel.Twitter) && (
        <>
          {/* twitter:* dùng name= (chuẩn Twitter Cards), không phải property= như Open Graph */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={href} />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDescription} />
          {image && <meta name="twitter:image" content={image} />}
        </>
      )}
      {/* Organization + JewelryStore: xuất trên mọi trang để AI/Google luôn thấy thông tin doanh nghiệp */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd()) }}
      />
      {/* Chỉ xuất Product JSON-LD khi có giá hợp lệ — thiếu offers/giá thì Google báo lỗi Product snippet */}
      {type === 'product' && typeof price === 'number' && price > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              '@id': `${href}#product`,
              name: (seo?.title || title).trim(),
              url: href,
              image: image ? [image] : undefined,
              description: schemaDescription || metaDescription,
              sku: productCode,
              mpn: productCode,
              productID: productId,
              brand: { '@type': 'Brand', name: BRAND },
              material: material || undefined,
              category: categoryName || category,
              offers: {
                '@type': 'Offer',
                url: href,
                priceCurrency: 'VND',
                price: price,
                priceValidUntil: priceValidUntil(30),
                itemCondition: 'https://schema.org/NewCondition',
                availability:
                  status === ProductStatus.Availability
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                seller: { '@id': BUSINESS.id },
                // Google Merchant listing: giao hàng miễn phí toàn quốc (policies/shipping-and-delivery), thu đổi tại cửa hàng (policies/refund)
                shippingDetails: {
                  '@type': 'OfferShippingDetails',
                  shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'VND' },
                  shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'VN' },
                },
                hasMerchantReturnPolicy: {
                  '@type': 'MerchantReturnPolicy',
                  applicableCountry: 'VN',
                  returnPolicyCategory: 'https://schema.org/MerchantReturnUnspecified',
                  returnMethod: 'https://schema.org/ReturnInStore',
                  merchantReturnLink: `${APP_URL}/policies/refund/`,
                },
              },
            }),
          }}
        />
      )}
    </NextHead>
  );
};
export default memo(MetaTag);
MetaTag.defaultProps = {
  image: `${process.env.APP_URL}/logo-bg.png`,
  blacklist: [],
  description: '',
  url: process.env.APP_URL,
};
